import type { FeatureKey } from "../../permissions/access";
import {
  BranchConflictField,
  BranchReadinessItemKey,
  BranchReadinessLevel,
  BranchReadinessStatus,
} from "../../types/branch.type";

export const BRANCH_LIST_PATH = "/branches";
export const branchDetailPath = (uuid: string) => `${BRANCH_LIST_PATH}/${uuid}`;

/** Menu title the sidebar filters by, kept beside the path it links to. */
export const BRANCH_MENU_TITLE = "สาขา";

/**
 * The screens where a missing readiness item is fulfilled. `/routes` and `/vehicles` are
 * their own pages; this checklist only points at them.
 */
const USER_MANAGEMENT_PATH = "/user-management";
const VEHICLES_PATH = "/vehicles";
const ROUTES_PATH = "/routes";
const PATHWAY_UPLOAD_PATH = "/branch/pathway/upload";

const OWNER_ADMIN_MASTER = "ADMIN / MASTER";
const OWNER_ADMIN_MASTER_GM = "ADMIN / MASTER · GM";
const OWNER_GM = "GM";

export type ChipColor = "default" | "success" | "warning" | "error";

/** What a readiness item needs, who does it, and on which page. */
export interface ReadinessGuide {
  label: string;
  owner: string;
  path: string;
  /** The link's text, naming the page. */
  linkLabel: string;
  /** The feature the viewer needs to open `path`; without it the link is not offered. */
  access?: FeatureKey;
}

const USER_PAGE = {
  path: USER_MANAGEMENT_PATH,
  linkLabel: "หน้าตั้งค่าผู้ใช้",
  access: "userManagement",
} as const;

export const READINESS_GUIDE: Record<BranchReadinessItemKey, ReadinessGuide> = {
  GM_USER: {
    label: "ผู้ใช้ผู้จัดการสาขา (GM) อย่างน้อย 1 คน",
    owner: OWNER_ADMIN_MASTER,
    ...USER_PAGE,
  },
  VEHICLE: {
    label: "รถอย่างน้อย 1 คัน",
    owner: OWNER_ADMIN_MASTER_GM,
    path: VEHICLES_PATH,
    linkLabel: "หน้ารถ",
  },
  FINANCE_USER: { label: "ผู้ใช้การเงิน", owner: OWNER_ADMIN_MASTER, ...USER_PAGE },
  ACCOUNTING_USER: { label: "ผู้ใช้บัญชี", owner: OWNER_ADMIN_MASTER, ...USER_PAGE },
  CHECKER_USER: { label: "ผู้ใช้ checker", owner: OWNER_ADMIN_MASTER, ...USER_PAGE },
  BACK_OFFICE_USER: {
    label: "ผู้ใช้ BO (การเงินและบัญชี)",
    owner: OWNER_ADMIN_MASTER,
    ...USER_PAGE,
  },
  SALES_USER: {
    label: "ผู้ใช้เซลส์ที่ผูกสายนี้ (routeCode)",
    owner: OWNER_ADMIN_MASTER,
    ...USER_PAGE,
  },
  STAFF: {
    label: "ข้อมูลพนักงานเซลส์ (staff) ของสายนี้",
    owner: `${OWNER_ADMIN_MASTER} (สร้างพร้อมผู้ใช้เซลส์)`,
    ...USER_PAGE,
  },
};

/** The per-route items, in the order the route table shows them as columns. */
export const ROUTE_ITEM_COLUMNS: { key: BranchReadinessItemKey; header: string }[] = [
  { key: "SALES_USER", header: "ผู้ใช้เซลส์" },
  { key: "STAFF", header: "พนักงานเซลส์ (staff)" },
];

/** Work the readiness gate does not check but a branch still needs before it can run. */
export const GENERAL_TASKS: ReadinessGuide[] = [
  {
    label: "สายปกติ 01–16",
    owner: OWNER_ADMIN_MASTER_GM,
    path: ROUTES_PATH,
    linkLabel: "หน้าสาย",
  },
  {
    label: "แผนวิ่ง",
    owner: OWNER_GM,
    path: PATHWAY_UPLOAD_PATH,
    linkLabel: "หน้าอัปโหลดแผนวิ่ง",
    access: "pathwayUpload",
  },
];

export const LEVEL_LABEL: Record<BranchReadinessLevel, string> = {
  REQUIRED: "บังคับ",
  RECOMMENDED: "แนะนำ",
};

export const ITEM_STATUS_LABEL: Record<BranchReadinessStatus, string> = {
  PASS: "ครบ",
  MISSING: "ขาด",
  UNKNOWN: "ตรวจสอบไม่ได้",
};

export const ROUTE_TEST_MODE_LABEL = "ทดสอบ";
export const ROUTE_OPEN_LABEL = "เปิดใช้แล้ว";

export const CONFLICT_FIELD_LABEL: Record<BranchConflictField, string> = {
  branch_number: "เลขสาขา",
  branch_prefix: "ตัวย่อ",
  branch_code: "รหัสสาขา",
  express_doc_40: "รหัสเอกสาร Express 40",
  express_doc_42: "รหัสเอกสาร Express 42",
};

export const CONFLICT_FIELD_ERROR = "ถูกใช้โดยสาขาอื่นแล้ว";
export const CREATE_FAILED_MESSAGE = "สร้างสาขาไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
export const UPDATE_FAILED_MESSAGE = "แก้ไขข้อมูลสาขาไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
export const OPEN_ROUTE_FAILED_MESSAGE = "เปิดใช้สายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
